'use client';

import type { Currency } from '@shared/utils';
import { useGetMatch, useGetPrize, useGetRank, useGetWinners } from '..';
import type { OddsEntry } from '../../types';

type UseMapPrizeTablesDefaultProps = {
  data: OddsEntry[];
  currency: Currency;
  namespace: [lotteryNamespace: string, generalNamespace: string];
  hideWinners: boolean;
};

/**
 * Maps lottery odds data into a format suitable for displaying prize tables.
 *
 * @param data - The array of odds entries for the lottery and any extra games.
 * @param currency - The currency to display prize amounts in.
 * @param namespace - Tuple containing the lottery and general translation namespaces.
 * @param hideWinners - (Optional) Use 'true' to hide winners.
 * @returns  An array of objects, each representing a prize table for a lottery or extra game.
 */
function useMapPrizeTablesDefault({ data, currency, namespace, hideWinners }: UseMapPrizeTablesDefaultProps) {
  const [lottery, general] = namespace;
  const getRank = useGetRank(lottery);
  const getMatch = useGetMatch(lottery);
  const getWinners = useGetWinners(general);
  const { default: getPrize } = useGetPrize(general);

  return (data ?? []).map(({ lotteryId, ranks }) => ({
    lotteryId,
    data: Object.entries(ranks ?? {}).map(([rank, conditions]) => ({
      rank: getRank(Number(rank)),
      match: getMatch(conditions[0]!.matches),
      ...(hideWinners ? {} : { winners: getWinners(conditions[0]?.winners) }),
      amount: getPrize(conditions[0]?.prize?.[currency]),
    })),
  }));
}

export { useMapPrizeTablesDefault };
